import React, { FC, useState } from 'react';
import { useEventCallback } from '@mui/material';
import { ToolBarButton } from '../ToolBarComponents';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import {
  showSelectOptionModal,
  showSelectOptionsModal,
} from '../../common/Dialog/Dialog';
import { StyledModal } from '../../common/Modal';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '../../common/ui-kit/buttons/Button';
import { Field } from '../../common/Field';
import { Input } from '../../common/ui-kit/inputs/Input';
import QuestionnaireIcon from '@lmt/unicorn-shared/app/icons/questionnaire.svg?react';

const isDev = process.env.NODE_ENV === 'development';

export const DialogTestScenariosButton: FC = () => {
  const modals = useModal();
  const forms = useGlobalForms();
  const [showStyledModal, setShowStyledModal] = useState(false);

  const openPicker = useEventCallback(async () => {
    await modals.showCustom<null>({
      title: 'Dialog test scenarios',
      okButtonText: 'Close',
      Component: (props) => (
        <ScenariosList
          modals={modals}
          onSelectStyledModal={() => {
            props.onClose(null);
            setShowStyledModal(true);
          }}
          onSelectChangeLanguage={() => {
            props.onClose(null);
            forms.changeLanguage();
          }}
        />
      ),
    });
  });

  if (!isDev) return null;

  return (
    <>
      <ToolBarButton
        icon={QuestionnaireIcon}
        title="Dialog test scenarios (dev)"
        onClick={openPicker}
        testId="toolbar-dialog-test-scenarios"
      />
      {showStyledModal && (
        <StyledModal
          isOpen={true}
          contentLabel="StyledModal sample"
          onRequestClose={() => setShowStyledModal(false)}
        >
          <div style={{ padding: 20, minWidth: 360 }}>
            Sample StyledModal body.
            <br />
            Verify: 18px bold title, dark header with 12/12/12/20 padding, 24×24
            close button containing a 12×12 icon, 4px rounded corners.
          </div>
        </StyledModal>
      )}
    </>
  );
};

type ScenariosListProps = {
  modals: ReturnType<typeof useModal>;
  onSelectStyledModal: () => void;
  onSelectChangeLanguage: () => void;
};

const ScenariosList: FC<ScenariosListProps> = ({
  modals,
  onSelectStyledModal,
  onSelectChangeLanguage,
}) => {
  const fire = (fn: () => unknown) => () => {
    void fn();
  };
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        minWidth: 360,
      }}
    >
      <Section title="Dialog.tsx (radio / checkbox pickers)">
        <ScenarioButton
          label="showSelectOptionModal (radio)"
          onClick={fire(() =>
            showSelectOptionModal(modals, {
              title: 'Select option',
              text: 'Pick one of the following options:',
              options: ['Option A', 'Option B', 'Option C'],
            }),
          )}
        />
        <ScenarioButton
          label="showSelectOptionsModal (checkbox)"
          onClick={fire(() =>
            showSelectOptionsModal(modals, {
              title: 'Select options',
              text: 'Pick any number of options:',
              options: [
                { id: 1, value: 'Option A' },
                { id: 2, value: 'Option B' },
                { id: 3, value: 'Option C' },
              ],
            }),
          )}
        />
      </Section>

      <Section title="useModal hook">
        <ScenarioButton
          label="showError"
          onClick={fire(() =>
            modals.showError({ text: 'Sample error message.' }),
          )}
        />
        <ScenarioButton
          label="showAlert"
          onClick={fire(() =>
            modals.showAlert({ text: 'Sample alert message.' }),
          )}
        />
        <ScenarioButton
          label="showConfirm"
          onClick={fire(() =>
            modals.showConfirm({
              title: 'Confirm action',
              text: 'Are you sure you want to proceed?',
            }),
          )}
        />
        <ScenarioButton
          label="showDeletionConfirmation"
          onClick={fire(() =>
            modals.showDeletionConfirmation({
              title: 'Delete item',
              text: 'This action is irreversible.',
              okButtonText: 'Delete',
            }),
          )}
        />
        <ScenarioButton
          label="showPrompt"
          onClick={fire(() =>
            modals.showPrompt({
              title: 'Enter a value',
              text: 'Sample prompt — type something below.',
              fieldName: 'Name',
              defaultValue: '',
            }),
          )}
        />
        <ScenarioButton
          label="showMultiButton"
          onClick={fire(() =>
            modals.showMultiButton({
              title: 'Choose path',
              text: 'Pick one of the options below.',
              buttons: [
                { id: 'a', text: 'Option A' },
                { id: 'b', text: 'Option B' },
                { id: 'c', text: 'Option C' },
              ],
            }),
          )}
        />
        <ScenarioButton
          label="showCustom (with form field)"
          onClick={fire(() =>
            modals.showCustom<{ name?: string }>({
              title: 'Custom dialog',
              okButtonText: 'Save',
              defaultValue: { name: '' },
              Component: (props) => (
                <Field title="Name" required>
                  <Input
                    autoFocus={true}
                    value={props.value?.name ?? ''}
                    onChange={(e) => props.setValue({ name: e.target.value })}
                  />
                </Field>
              ),
            }),
          )}
        />
      </Section>

      <Section title="StyledModal (welcome / launcher chrome)">
        <ScenarioButton
          label="Open StyledModal sample"
          onClick={onSelectStyledModal}
        />
      </Section>

      <Section title="PopupWindow (global-forms)">
        <ScenarioButton
          label="Open ChangeLanguage form"
          onClick={onSelectChangeLanguage}
        />
      </Section>
    </div>
  );
};

const Section: FC<React.PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div
      style={{
        fontWeight: 'bold',
        fontSize: 13,
        color: 'var(--normal)',
        borderBottom: '1px solid var(--container-border)',
        paddingBottom: 4,
      }}
    >
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {children}
    </div>
  </div>
);

const ScenarioButton: FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <Button
    color={ButtonColor.Primary}
    width={ButtonWidth.Fullwidth}
    title={label}
    onClick={onClick}
  />
);
